import type { ReactElement } from 'react';

import FaqPageDialogContent from '@/features/userManual/components/FaqPageDialogContent';
import FindChargingStationsPageDialogContent from '@/features/userManual/components/FindChargingStationsPageDialogContent';
import UserManualFunctionDialogContent from '@/features/userManual/components/UserManualFunctionDialogContent';
import WelcomePageDialogContent from '@/features/userManual/components/WelcomePageDialogContent';
import type { FindChargingStationsPageConfig, IPagesForUserManualDialog } from '@/features/userManual/utils';
import { userManualSections } from '@/features/userManual/utils';

import WhatsNewPageDialogContent from './WhatsNew/WhatsNewPageDialogContent';

const FIND_STATION_PAGES = new Set([
    userManualSections.findChargingStations.fleetMonitor,
    userManualSections.findChargingStations.driverApp,
    userManualSections.findChargingStations.manEtruck,
]);

const FUNCTION_PAGES = new Set([
    userManualSections.functions.overview,
    userManualSections.functions.chargingCards,
    userManualSections.functions.activateCard,
    userManualSections.functions.chargingCardAlias,
    userManualSections.functions.orderChargingCard,
    userManualSections.functions.contractInformation,
    userManualSections.functions.contactDetails,
    userManualSections.functions.invoiceDetails,
    userManualSections.functions.chargingSessions,
    userManualSections.functions.detailChargingSessions,
    userManualSections.functions.invoices,
    userManualSections.functions.download,
]);

type UserManualSection = 'welcome' | 'whatsNew' | 'faq' | 'findChargingStations' | 'functions';

const USER_MANUAL_SECTION_MAP: Record<string, UserManualSection> = {
    [userManualSections.service.welcome]: 'welcome',
    [userManualSections.service.whatsNew]: 'whatsNew',
    [userManualSections.service.faq]: 'faq',
    ...Object.fromEntries([...FIND_STATION_PAGES].map(p => [p, 'findChargingStations'])),
    ...Object.fromEntries([...FUNCTION_PAGES].map(p => [p, 'functions'])),
};

type UserManualPageProps = IPagesForUserManualDialog | FindChargingStationsPageConfig;

const COMPONENT_MAP: Record<UserManualSection, (props: UserManualPageProps) => ReactElement> = {
    welcome: props => {
        const p = props as IPagesForUserManualDialog;
        return (
            <WelcomePageDialogContent
                keyPage={p.keyPage}
                imageSrc={p.imageSrc}
                listKeys={p.listKeys}
                listSubtitles={p.listSubtitles}
            />
        );
    },
    whatsNew: () => <WhatsNewPageDialogContent />,
    faq: () => <FaqPageDialogContent />,
    findChargingStations: props => {
        const p = props as FindChargingStationsPageConfig;
        return (
            <FindChargingStationsPageDialogContent
                keyPage={p.keyPage}
                images={p.images}
                subtitles={p.subtitles}
                introText={p.introText}
                counterText={p.counterText}
                footnotes={p.footnotes}
                footnoteLink={p.footnoteLink}
            />
        );
    },
    functions: props => {
        const p = props as IPagesForUserManualDialog;
        return <UserManualFunctionDialogContent keyPage={p.keyPage} imageSrc={p.imageSrc} listKeys={p.listKeys} />;
    },
};

export const UserManualPages = (props: IPagesForUserManualDialog | FindChargingStationsPageConfig) => {
    const section = USER_MANUAL_SECTION_MAP[props.keyPage] ?? 'welcome';

    const Component = COMPONENT_MAP[section];

    return <Component {...props} />;
};
